import React from 'react';

/**
 * ToggleSwitch — single reusable toggle used across editor + admin.
 *
 * Props:
 *   enabled   {boolean}  — controlled on/off state
 *   onToggle  {function} — called on click or Space/Enter
 *   size      {'sm'|'md'} — sm: 34×18, md: 42×22 (default)
 *   disabled  {boolean}  — greys out and blocks interaction
 *
 * Geometry (md):  track 42×22, padding 2px, thumb 18×18, travel 20px
 * Geometry (sm):  track 34×18, padding 2px, thumb 14×14, travel 16px
 *
 * overflow:hidden on the track is a hard clip — the thumb can never escape
 * regardless of transform value. flex layout eliminates absolute positioning
 * so there is no top/left drift across browsers.
 */

const SIZES = {
  sm: { w: 34, h: 18, thumb: 14, padding: 2 },
  md: { w: 42, h: 22, thumb: 18, padding: 2 },
};

const ToggleSwitch = ({ enabled, onToggle, size = 'md', disabled = false }) => {
  const s = SIZES[size] || SIZES.md;
  const travel = s.w - s.padding * 2 - s.thumb; // exact pixel travel

  const handleKeyDown = (e) => {
    if ((e.key === ' ' || e.key === 'Enter') && !disabled) {
      e.preventDefault();
      onToggle?.();
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={!disabled ? onToggle : undefined}
      onKeyDown={handleKeyDown}
      style={{
        width: s.w,
        height: s.h,
        borderRadius: s.h / 2,
        padding: s.padding,
        boxSizing: 'border-box',
        background: enabled
          ? 'linear-gradient(135deg, #8b5cf6, #6366f1)'
          : '#d1d5db',
        transition: 'background 0.25s ease',
        flexShrink: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: 'none',
        outline: 'none',
        overflow: 'hidden',   // hard clip — thumb never escapes track
        display: 'flex',
        alignItems: 'center', // vertical centering, no absolute positioning
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          display: 'block',
          width: s.thumb,
          height: s.thumb,
          borderRadius: '50%',
          background: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          transform: enabled ? `translateX(${travel}px)` : 'translateX(0)',
          transition: 'transform 0.25s ease',
          willChange: 'transform',
          flexShrink: 0,
        }}
      />
    </button>
  );
};

export default ToggleSwitch;
